/**
 * MandateStakeholders — Stakeholder Map.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ExternalLink } from 'lucide-react';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useProductSurface } from '../../hooks/useProductSurface';
import { PageHeader } from '../../components/prioritization/PageHeader';
import type { MandateStakeholder, StakeholderInfluence, StakeholderStance } from '../../types/mandate';
import type { AddStakeholderInput, UpdateStakeholderInput } from '../../stores/mandate/mandateStore';

const STANCE_BADGE: Record<StakeholderStance, string> = {
  ally: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  'fence-sitter': 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  skeptic: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  opponent: 'bg-[var(--coral-dim)] text-[var(--coral)]',
};

const STANCE_ORDER: StakeholderStance[] = ['ally', 'fence-sitter', 'skeptic', 'opponent'];

interface StakeholderFormState {
  name: string;
  role: string;
  influence: StakeholderInfluence | '';
  stance: StakeholderStance | '';
  trustGaps: string;
  notes: string;
}

const EMPTY_FORM: StakeholderFormState = {
  name: '',
  role: '',
  influence: '',
  stance: '',
  trustGaps: '',
  notes: '',
};

function stakeholderToForm(s: MandateStakeholder): StakeholderFormState {
  return {
    name: s.name,
    role: s.role ?? '',
    influence: s.influence ?? '',
    stance: s.stance ?? '',
    trustGaps: s.trustGaps ?? '',
    notes: s.notes ?? '',
  };
}

export default function MandateStakeholders() {
  const userId = useUserStore((s) => s.userId);
  const { to } = useProductSurface();
  const {
    stakeholders,
    addStakeholder,
    updateStakeholder,
    deleteStakeholder,
    promoteStakeholderToItem,
  } = useMandateStore();

  const [showForm, setShowForm] = useState(false);
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<StakeholderFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  function openAdd() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(s: MandateStakeholder) {
    setForm(stakeholderToForm(s));
    setEditingId(s.id);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function setField(field: keyof StakeholderFormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handlePromote(s: MandateStakeholder) {
    if (!userId || s.platformItemId) return;
    setPromotingId(s.id);
    try {
      await promoteStakeholderToItem(s.id, userId);
    } finally {
      setPromotingId(null);
    }
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        const updates: UpdateStakeholderInput = {
          name: form.name.trim(),
          role: form.role.trim() || null,
          influence: (form.influence as StakeholderInfluence) || null,
          stance: (form.stance as StakeholderStance) || null,
          trustGaps: form.trustGaps.trim() || null,
          notes: form.notes.trim() || null,
        };
        await updateStakeholder(editingId, updates);
      } else {
        const data: AddStakeholderInput = {
          name: form.name.trim(),
          role: form.role.trim() || undefined,
          influence: (form.influence as StakeholderInfluence) || undefined,
          stance: (form.stance as StakeholderStance) || undefined,
          trustGaps: form.trustGaps.trim() || undefined,
          notes: form.notes.trim() || undefined,
        };
        await addStakeholder(data);
      }
      closeForm();
    } finally {
      setSaving(false);
    }
  }

  // Group by influence, then sort by stance within groups
  const INFLUENCE_GROUPS: { key: StakeholderInfluence; label: string }[] = [
    { key: 'high', label: 'High Influence' },
    { key: 'medium', label: 'Medium Influence' },
    { key: 'low', label: 'Low Influence' },
  ];

  const unassigned = stakeholders.filter((s) => !s.influence);

  function sortByStance(arr: MandateStakeholder[]): MandateStakeholder[] {
    return [...arr].sort((a, b) => {
      const ia = a.stance ? STANCE_ORDER.indexOf(a.stance) : 999;
      const ib = b.stance ? STANCE_ORDER.indexOf(b.stance) : 999;
      return ia - ib;
    });
  }

  return (
    <div>
      <PageHeader
        icon={<Users className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Stakeholder Map"
        subtitle="Who has influence, and where you stand"
        actions={
          <button
            onClick={openAdd}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 text-sm"
          >
            + Add stakeholder
          </button>
        }
      />

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-4">
            {editingId ? 'Edit stakeholder' : 'Add stakeholder'}
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                Name <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Full name"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Role / Title</label>
              <input
                type="text"
                value={form.role}
                onChange={(e) => setField('role', e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g. CFO"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Influence</label>
              <select
                value={form.influence}
                onChange={(e) => setField('influence', e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Stance</label>
              <select
                value={form.stance}
                onChange={(e) => setField('stance', e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                <option value="ally">Ally</option>
                <option value="fence-sitter">Fence-sitter</option>
                <option value="skeptic">Skeptic</option>
                <option value="opponent">Opponent</option>
              </select>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Trust gaps</label>
              <p className="text-xs text-[var(--text-ter)] mb-1">
                Where are the gaps? (competence, reliability, judgment, integrity)
              </p>
              <textarea
                value={form.trustGaps}
                onChange={(e) => setField('trustGaps', e.target.value)}
                rows={2}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                Notes <span className="text-[var(--text-ter)] font-normal">(optional)</span>
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                rows={2}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={handleSave}
              disabled={!form.name.trim() || saving}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2 text-sm"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add stakeholder'}
            </button>
            <button
              onClick={closeForm}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Stakeholder list grouped by influence */}
      {stakeholders.length === 0 ? (
        <p className="text-sm text-[var(--text-ter)] text-center py-8">
          No stakeholders mapped yet. Add your first stakeholder above.
        </p>
      ) : (
        <div className="space-y-6">
          {INFLUENCE_GROUPS.map(({ key, label }) => {
            const group = sortByStance(stakeholders.filter((s) => s.influence === key));
            if (group.length === 0) return null;
            return (
              <div key={key}>
                <h2 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
                  {label}
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {group.map((s) => (
                    <StakeholderCard
                      key={s.id}
                      stakeholder={s}
                      onEdit={() => openEdit(s)}
                      onDelete={() => deleteStakeholder(s.id)}
                      onPromote={() => handlePromote(s)}
                      promoting={promotingId === s.id}
                      itemHref={(id) => to(`/items/${id}`)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {unassigned.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
                Unassigned
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {sortByStance(unassigned).map((s) => (
                  <StakeholderCard
                    key={s.id}
                    stakeholder={s}
                    onEdit={() => openEdit(s)}
                    onDelete={() => deleteStakeholder(s.id)}
                    onPromote={() => handlePromote(s)}
                    promoting={promotingId === s.id}
                    itemHref={(id) => to(`/items/${id}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StakeholderCard({
  stakeholder: s,
  onEdit,
  onDelete,
  onPromote,
  promoting,
  itemHref,
}: {
  stakeholder: MandateStakeholder;
  onEdit: () => void;
  onDelete: () => void;
  onPromote: () => void;
  promoting: boolean;
  itemHref: (id: string) => string;
}) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 group">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-semibold text-[var(--text-pri)]">{s.name}</span>
            {s.stance && (
              <span
                className={`text-xs font-medium px-1.5 py-0.5 rounded ${STANCE_BADGE[s.stance]}`}
              >
                {s.stance}
              </span>
            )}
          </div>
          {s.role && <p className="text-sm text-[var(--text-ter)]">{s.role}</p>}
          {s.trustGaps && (
            <p className="text-xs text-[var(--text-ter)] mt-1">Trust gaps: {s.trustGaps}</p>
          )}
          {s.notes && (
            <p className="text-xs text-[var(--text-ter)] mt-1 line-clamp-2">{s.notes}</p>
          )}
        </div>
        <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {s.platformItemId ? (
            <Link
              to={itemHref(s.platformItemId)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded hover:bg-primary-50 inline-flex items-center gap-1"
            >
              Item <ExternalLink className="h-3 w-3" />
            </Link>
          ) : (
            <button
              onClick={onPromote}
              disabled={promoting}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded hover:bg-primary-50 disabled:opacity-50"
            >
              {promoting ? '…' : 'Promote'}
            </button>
          )}
          <button
            onClick={onEdit}
            className="text-xs text-[var(--text-ter)] hover:text-primary-600 font-medium px-2 py-1 rounded hover:bg-primary-50"
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="text-xs text-[var(--text-ter)] hover:text-[var(--coral)] font-medium px-2 py-1 rounded hover:bg-[var(--coral-dim)]"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
